import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FadeIn } from "../../components/fade-in/fade-in";
import { FullScreenLoader } from "../../components/full-screen-loader/full-screen-loader";
import { MstrButtonOutline } from "../../components/mstr-button-outline/mstr-button-outline";
import { MstrButton } from "../../components/mstr-button/mstr-button";
import { StaggerList } from "../../components/stagger-list/stagger-list";
import {
  ChapterEntity,
  CohortEntity,
  InitiativeWithProgressOutput,
  LearnerDetailsOutput,
  PackageUnitEntity,
  RefdocCountOutput,
  UnitEntity,
} from "../../generated/graphql";
import { ReferenceLibrary } from "../../partials/reference-library/reference-library";
import "./home.scss";
import { InitiativeIntro } from "./initiative-intro/initiative-intro";
import { InitiativeItem } from "./initiative-item/initiative-item";
import { WelcomeMessage } from "./welcome/welcome";
import { PackUnitItem } from "./pack-init-item";
import { iPackUnitGroup } from "../../types/types";
import { InitiativePackageItem } from "./initiative-item/initiative-item-package";
import { CONSTANTS } from "../../constants";

interface ReassignInitiativeData {
  reassignInitiativeToLearner: UnitEntity[];
}

const GET_INITIATIVES = gql`
  query Section {
    getInitiativesForLearnerWithProgress {
      unit {
        id
        name
        parent_unit
        is_private
        reassigned_display_name
        intro
      }
      questionCount
      seen_intro
      progress
      isLastActiveInitiative
      assignmentDate
    }
    getUserCohorts
    getAllPackages {
      id
      name
      sort_id
      units {
        id
        name
      }
      cohorts {
        id
      }
      unit_sequence
    }
    getReferenceDocumentsForLearner {
      id
      chapter_name
      refdocs {
        id
        document_name
        url
        reftype
      }
    }
    getRefdocViewCount {
      document_id
      count
    }
    getLearnerDetails {
      id
      first_name
      last_name
      user_id_state
      is_doctor
    }
  }
`;

const REASSIGN_INITIATIVE = gql`
  mutation ReassignInitiativeToLearner($initiative_id: Int!) {
    reassignInitiativeToLearner(initiative_id: $initiative_id) {
      id
      name
    }
  }
`;

type RowWithPack = {
  package: PackageUnitEntity;
  row: InitiativeWithProgressOutput;
}

interface LearnerInitiativesData {
  getInitiativesForLearnerWithProgress: InitiativeWithProgressOutput[];
  getLearnerDetails: LearnerDetailsOutput;
  getRefdocViewCount: RefdocCountOutput[];
  getReferenceDocumentsForLearner: [ChapterEntity];
  getAllPackages: PackageUnitEntity[];
  getUserCohorts: number[];
}

export const HomeScreen = () => {
  const { loading, error, data, refetch } = useQuery<LearnerInitiativesData>(
    GET_INITIATIVES,
    {
      variables: { user_id: 1 },
      fetchPolicy: "no-cache",
    }
  );

  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();

  const [packIndexOpen, setPackIndexOpen] = useState(0);

  useEffect(() => {
    const redirectLink = localStorage.getItem("redirectLink");
    if (redirectLink) {
      localStorage.removeItem("redirectLink");
      window.location.href = redirectLink;
    }
  }, []);

  // Force user to change their password
  if (data) {
    if (data.getLearnerDetails.user_id_state === "PR") {
      navigate("/password-reset");
    }
  }

  let initiatives: InitiativeWithProgressOutput[] = [];

  let packageUnits: { [k: string]: number[] } = {};


  for (const pack of data?.getAllPackages || []) {
    packageUnits[pack.id] = [];
    for (const unit of pack.units) {
      packageUnits[pack.id].push(+unit.id);
    }
  }

  if (data && data.getInitiativesForLearnerWithProgress) {

    // Sort by assignment date. Last assigned initiative on top
    initiatives = data!.getInitiativesForLearnerWithProgress.sort((a, b) => {
      return b.assignmentDate - a.assignmentDate;
    });

    // Move last active initiative on top
    initiatives = initiatives.sort((a) => {
      return a.isLastActiveInitiative ? -1 : 1;
    });

    const completedInitiatives = initiatives!.filter((i) => i.progress >= 100);
    const inProgressInitiatives = initiatives!.filter((i) => i.progress < 100);

    // Incomplete initiatives on top, completed initiatives after that
    initiatives = [...inProgressInitiatives, ...completedInitiatives];
  }

  let groups: iPackUnitGroup[] = [];

  for (const pack of data?.getAllPackages || []) {
    const group: iPackUnitGroup = {
      pack,
      units: [],
      completionPercentage: 0,
    };

    const packCohortIds = pack.cohorts.map((c: CohortEntity) => c.id);
    const userCohortIds = data?.getUserCohorts || [];

    const userInPackCohort = packCohortIds.some((c: number) => userCohortIds.includes(c));

    if (!userInPackCohort) {
      continue;
    }

    for (const initiative of initiatives) {
      let unitIdToCheckInPackage = +initiative.unit.id;

      if (initiative.unit.is_private) {
        unitIdToCheckInPackage = initiative.unit.parent_unit;
      }

      if (packageUnits[pack.id].includes(unitIdToCheckInPackage)) {
        group.units.push(initiative);
      }
    }

    group.completionPercentage = group.units.reduce((acc, curr) => {
      return acc + curr.progress;
    }, 0) / group.units.length;

    const unitSequence = (pack.unit_sequence || { order: [] }).order.map((row: any) => {
      return row.id;
    });

    group.units = group.units.sort((a, b) => {
      const aIndex = unitSequence.indexOf(+a.unit.id);
      const bIndex = unitSequence.indexOf(+b.unit.id);
      const diff = aIndex - bIndex;
      return diff;
    });

    // const completedInitiatives = group.units.filter((i) => i.progress >= 100);
    // const inProgressInitiatives = group.units.filter((i) => i.progress < 100);

    // group.units = [...inProgressInitiatives, ...completedInitiatives];

    groups.push(group);
  }

  groups = groups.sort((a, b) => {
    return a.pack.sort_id - b.pack.sort_id;
  });

  let initiativesWithoutGroups: InitiativeWithProgressOutput[] = [];

  for (const initiative of initiatives) {
    let found = false;
    for (const group of groups) {
      if (packageUnits[group.pack.id].includes(+initiative.unit.id)) {
        found = true;
        break;
      }
    }
    if (!found) {
      initiativesWithoutGroups.push(initiative);
    }
  }

  groups.push({
    pack: {
      id: "0",
      name: CONSTANTS.INITIATIVES_WITHOUT_PACKAGE_TITLE,
      units: [],
      cohorts: [],
      unit_sequence: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      users: [],
      pack_users: [],
      updated_by: 0,
      sort_id: 99,
    },
    units: initiativesWithoutGroups,
    completionPercentage: 0,
  });

  groups.forEach((group) => {
    console.log(group.pack.name + " " + group.completionPercentage);
  });

  const completedGroups = groups.filter((group) => group.completionPercentage >= 100);
  const inProgressGroups = groups.filter((group) => group.completionPercentage < 100);

  groups = [...inProgressGroups, ...completedGroups];

  // for (const group of groups) {
  //   if (group.units.length === 0) {
  //     continue;
  //   }
  //   console.log("Package: " + group.pack.name);
  //   for (const unit of group.units) {
  //     console.log("Unit: " + unit.unit.name);
  //   }
  //   console.log("--------------------------------");
  // }

  // console.log("Initiatives without groups");
  // for (const initiative of initiativesWithoutGroups) {
  //   console.log("Unit: " + initiative.unit.name);
  // }

  const completedInitiatives = initiativesWithoutGroups.filter((i) => i.progress >= 100);
  const inProgressInitiatives = initiativesWithoutGroups.filter((i) => i.progress < 100);

  initiativesWithoutGroups = [...inProgressInitiatives, ...completedInitiatives];

  const recentInitiatives = initiativesWithoutGroups.slice(0, 6);
  const otherInitiatives = initiativesWithoutGroups.slice(6, 200);



  const [selectedInitiative, setInitiative] = useState<UnitEntity>();
  const [initiativesShown, setInitiativesShown] = useState(true);
  const [
    initiativeToReassign,
    setInitiativeToReassign,
  ] = useState<UnitEntity>();

  const [reassignInitiative] = useMutation<ReassignInitiativeData>(
    REASSIGN_INITIATIVE,
    {
      update: (cache, response) => {
        refetch();
        setInitiativeToReassign(undefined);
      },
    }
  );

  const startInitiative = (initiative: UnitEntity) => {
    navigate(`/initiative/${initiative.id}`);
  };

  const getJsxForInitiative = (
    initiativeWithProgress: InitiativeWithProgressOutput,
    packageName?: string,
  ) => {
    const initiative = initiativeWithProgress.unit;

    console.log("Initiative Item: ", initiativeWithProgress.unit.name, packageName);

    const Comp = packageName ? InitiativePackageItem : InitiativeItem;

    return (
      <div key={initiativeWithProgress.unit.id}>
        <Comp
          onClick={() => {
            if (initiativeWithProgress.progress >= 100) {
              setInitiativeToReassign(initiativeWithProgress.unit);
            } else {
              const key = `intro-${initiative.id}`;
              const introShown = initiativeWithProgress.seen_intro;
              if (introShown || !initiative.intro) {
                startInitiative(initiative);
              } else {
                navigate(`/initiative-intro/${initiative.id}`);
                // setInitiative(initiative);
              }
            }
          }}
          packageName={packageName}
          allInitiatives={initiatives}
          showProgressBar={true}
          initiative={initiative}
          progress={initiativeWithProgress.progress}
        />
      </div>
    );
  };

  if (loading) return <FullScreenLoader />;
  if (error) return <div>
    <Navigate to="/login" />
  </div>;

  const refdocCount: { [k: string]: number } = {};
  if (data?.getRefdocViewCount) {
    for (let row of data?.getRefdocViewCount) {
      refdocCount[row.document_id] = row.count || 0;
    }
  }

  let firstInitiativename = '';
  if (recentInitiatives.length) {
    const firstUnit = recentInitiatives[0].unit;
    if (firstUnit.is_private) {
      firstInitiativename = firstUnit.name.split("-")[0];
    } else {
      firstInitiativename = firstUnit.name;
    }
  }

  let rowsWithPackages: RowWithPack[] = [];
  for (const group of groups) {
    for (const row of group.units) {
      rowsWithPackages.push({
        package: group.pack,
        row,
      })
    }
  }

  const completedInitiatives2 = rowsWithPackages.filter((row) => row.row.progress >= 100);
  const inProgressInitiatives2 = rowsWithPackages.filter((row) => row.row.progress < 100);
  rowsWithPackages = [...inProgressInitiatives2, ...completedInitiatives2];

  let refdoclistdata: ChapterEntity[] = JSON.parse(JSON.stringify(data?.getReferenceDocumentsForLearner));
  const initiativeNameSequence: string[] = rowsWithPackages.map((row) => {
    return row.row.unit.name;
  });

  refdoclistdata.sort((a, b) => {
    const aIndex = initiativeNameSequence.indexOf(a.chapter_name);
    const bIndex = initiativeNameSequence.indexOf(b.chapter_name);
    return aIndex - bIndex;
  });

  if (!selectedInitiative) {
    return (
      <div className="initiative-home-screen">
        <WelcomeMessage details={data?.getLearnerDetails!} />
        <div className="px-4">
          {initiativeToReassign && (
            <div className="ms-modal">
              <div className="modal-content">
                <div style={{ fontSize: 18 }}>
                  Would you like to go through this initiative again?
                </div>
                <br />
                <br />
                <div style={{ display: "flex" }}>
                  <MstrButton
                    width={100}
                    onClick={() => {
                      reassignInitiative({
                        variables: {
                          initiative_id: +initiativeToReassign!.id,
                        },
                      });
                    }}
                    text="Yes"
                  />
                  <MstrButtonOutline
                    width={100}
                    onClick={() => {
                      setInitiativeToReassign(undefined);
                    }}
                    text="No"
                  />
                </div>
              </div>
            </div>
          )}

          {/* {groups.map((group, index) => {
            if (group.units.length === 0) {
              return null;
            }
            return (
              <PackUnitItem pack={group} isExpanded={packIndexOpen === index} onExpand={(index) => setPackIndexOpen(index)} index={index}>
                {group.units.map((row) => {
                  return getJsxForInitiative(row, group.pack.name);
                })}
              </PackUnitItem>
            )
          })} */}

          <div className="text-xl font-semibold flex-1 items-center mt-4">
            My Initiatives
          </div>

          {rowsWithPackages.slice(0, 6).map(row => {
            return getJsxForInitiative(row.row, row.package.name);
          })}

          {showMore && (
            <div>
              {rowsWithPackages.slice(6).map(row => {
                return getJsxForInitiative(row.row, row.package.name);
              })}
            </div>
          )}

          {initiativesShown &&
            rowsWithPackages.length > 6 && (
              <div
                className="more-less-button"
                onClick={() => setShowMore(!showMore)}
              >
                {showMore ? "LESS ▲" : "MORE ▼"}
              </div>
            )}

        </div>

        <br />
        {initiativesShown && (
          <FadeIn>
            <ReferenceLibrary
              refetchInitiatives={async () => {
                await refetch();
              }}
              assignedInitiativeIds={initiatives.map(i => +i.unit.id)}
              refdocCount={refdocCount} firstInitiativename={firstInitiativename} data={refdoclistdata} />
          </FadeIn>
        )}
      </div>
    );
  } else {
    return (
      <InitiativeIntro
        learnerDetails={data?.getLearnerDetails!}
        initiative={selectedInitiative}
      />
    );
  }
};
